<template>
  <div
    v-on-clickaway="deactivate"
    :tabindex="tabindex"
    @focus="activate"
    @blur="onBlur"
    @keydown.self.down.prevent="pointerForward"
    @keydown.self.up.prevent="pointerBackward"
    @keydown.enter.tab.stop.self="addPointerElement($event)"
    @keyup.esc="deactivate"
    :class="wrapperClasses"
  >
    <slot name="caret" :toggle="toggle">
      <div @mousedown.prevent.stop="toggle" class="multiselect__select">
        <div class="multiselect__select--arrow"></div>
      </div>
    </slot>
    <slot name="clear" :search="search"></slot>
    <div ref="tags" class="multiselect__tags" :class="multiselectClass">
      <div class="multiselect__tags-wrap" v-show="visibleValue.length > 0">
        <template v-for="option of visibleValue" @mousedown.prevent>
          <slot
            name="tag"
            :option="option"
            :search="search"
            :remove="removeElement"
          >
            <span class="multiselect__tag">
              <span v-text="getOptionLabel(option)"></span>
              <i
                aria-hidden="true"
                tabindex="1"
                @keydown.enter.prevent="removeElement(option)"
                @mousedown.prevent="removeElement(option)"
                class="multiselect__tag-icon"
              ></i>
            </span>
          </slot>
        </template>
      </div>
      <template v-if="internalValue && internalValue.length > limit">
        <strong
          class="multiselect__strong"
          v-text="limitText(internalValue.length - limit)"
        ></strong>
      </template>
      <transition name="multiselect__loading">
        <slot name="loading"
          ><div v-show="loading" class="multiselect__spinner"></div
        ></slot>
      </transition>
      <label
        v-if="isLabelVisible"
        class="text-field--label"
        :class="labelClass"
      >
        {{ label }}
      </label>

      <span
        v-if="currentOptionLabel"
        class="multiselect__single"
        :class="placeholderClass"
        @mousedown.prevent="toggle"
        v-text="currentOptionLabel"
      />
    </div>

    <transition name="multiselect">
      <div
        v-show="isOpen"
        class="multiselect__content-wrapper"
        ref="list"
        :style="contentWrapperStyles"
      >
        <ul class="multiselect__content" :style="contentStyle">
          <slot name="beforeList" />
          <li
            v-if="searchable"
            class="multiselect__option multiselect__search"
            data-sticky="true"
          >
            <a-text-input
              v-model="search"
              searchIcon
              :placeholder="searchFieldPlaceholder"
              @keyup.esc="deactivate"
              @keydown.down.prevent="pointerForward"
              @keydown.up.prevent="pointerBackward"
              @keydown.enter.prevent.stop.self="addPointerElement($event)"
              @keydown.delete.stop="removeLastElement"
            />
          </li>

          <li v-if="multiple && max === internalValue.length">
            <span class="multiselect__option">
              <slot name="maxElements"
                >Maximum of {{ max }} options selected. First remove a selected
                option to select another.</slot
              >
            </span>
          </li>

          <template v-if="!max || internalValue.length < max">
            <li
              v-for="(option, index) of filteredOptions"
              :key="index"
              v-focusable
              class="multiselect__element"
              @click="select(option)"
              @blur="onBlur"
            >
              <div
                v-if="!(option && (option.$isLabel || option.$isDisabled))"
                :class="optionHighlight(index, option)"
                @mouseenter.self="pointerSet(index)"
                :data-select="
                  option && option.isTag ? tagPlaceholder : selectLabelText
                "
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
                class="multiselect__option"
              >
                <a-checkbox
                  v-if="multiple"
                  :value="isSelected(option)"
                  style="pointer-events: none;"
                >
                  <slot name="option" :option="option" :search="search">
                    <span>{{ getOptionLabel(option) }}</span>
                  </slot>
                </a-checkbox>

                <slot v-else name="option" :option="option" :search="search">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </div>
              <span
                v-if="option && (option.$isLabel || option.$isDisabled)"
                :class="optionHighlight(index, option)"
                class="multiselect__option multiselect__option--disabled"
              >
                <slot name="option" :option="option" :search="search">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
            </li>
          </template>
          <li v-show="isShowNoResultVisible" class="multiselect__element">
            <span
              class="multiselect__option multiselect__option_default-cursor"
            >
              <slot name="noResult">
                <p class="multiselect__no-result">No elements found.</p>
              </slot>
            </span>
          </li>
          <slot name="afterList"></slot>
        </ul>
      </div>
    </transition>
    <a-validation-message :error="error" />
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway'

import mixins from '@/utils/mixins'
import multiselectMixin from './multiselectMixin'
import pointerMixin from './pointerMixin'
import AValidationMessage from 'shared/AValidationMessage'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'

export default {
  name: 'vue-multiselect',
  mixins: [multiselectMixin, pointerMixin, mixins.validation, hydrationHelpers],
  directives: { onClickaway },
  components: { AValidationMessage },
  props: {
    /**
     * name attribute to match optional label element
     * @default ''
     * @type {String}
     */
    name: propValidator([PROP_TYPES.STRING], false, ''),
    /**
     * String to show when pointing to an option
     * @default 'Press enter to select'
     * @type {String}
     */
    selectLabel: propValidator(
      [PROP_TYPES.STRING],
      false,
      'Press enter to select'
    ),
    /**
     * String to show next to selected option
     * @default 'Selected'
     * @type {String}
     */
    selectedLabel: propValidator([PROP_TYPES.STRING], false, 'Selected'),
    /**
     * String to show when pointing to an alredy selected option
     * @default 'Press enter to remove'
     * @type {String}
     */
    deselectLabel: propValidator(
      [PROP_TYPES.STRING],
      false,
      'Press enter to remove'
    ),
    /**
     * Decide whether to show pointer labels
     * @default true
     * @type {Boolean}
     */
    showLabels: propValidator([PROP_TYPES.BOOLEAN], false, true),
    /**
     * Limit the display of selected options. The rest will be hidden within the limitText string.
     * @default 99999
     * @type {Integer}
     */
    limit: propValidator([PROP_TYPES.NUMBER], false, 99999),
    /**
     * Sets maxHeight style value of the dropdown
     * @default 300
     * @type {Integer}
     */
    maxHeight: propValidator([PROP_TYPES.NUMBER], false, 300),
    /**
     * Function that process the message shown when selected
     * elements pass the defined limit.
     * @default 'and * more'
     * @param {Int} count Number of elements more than limit
     * @type {Function}
     */
    limitText: propValidator([PROP_TYPES.FUNCTION], false, () => count =>
      `and ${count} more`
    ),
    /**
     * Set true to trigger the loading spinner.
     * @default False
     * @type {Boolean}
     */
    loading: propValidator([PROP_TYPES.BOOLEAN], false, false),
    /**
     * Disables the multiselect if true.
     * @default false
     * @type {Boolean}
     */
    disabled: propValidator([PROP_TYPES.BOOLEAN], false, false),
    /**
     * Fixed opening direction
     * @default ''
     * @type {String}
     */
    openDirection: propValidator([PROP_TYPES.STRING], false, ''),
    showNoResults: propValidator([PROP_TYPES.BOOLEAN], false, true),
    tabindex: propValidator([PROP_TYPES.NUMBER], false, 0),
    error: propValidator([PROP_TYPES.OBJECT], false, () => ({})),
    required: propValidator([PROP_TYPES.BOOLEAN], false, false),
    label: propValidator([PROP_TYPES.STRING], false, ''),
    persistentLabel: propValidator([PROP_TYPES.BOOLEAN], false, false),
    withMobileLayout: propValidator([PROP_TYPES.BOOLEAN], false, true)
  },
  computed: {
    multiselectClass() {
      return {
        'multiselect__tags--active': this.isOpen,
        error: this.$_validation_hasValidationError(this.error)
      }
    },
    labelClass() {
      return {
        required: this.required
      }
    },
    placeholderClass() {
      return {
        required: this.required && !this.isLabelVisible && !this.isSelectedValue
      }
    },
    isLabelVisible() {
      return (
        (this.isSelectedValue || this.persistentLabel) &&
        this.label &&
        !this.isOpen
      )
    },
    isShowNoResultVisible() {
      return (
        this.showNoResults &&
        this.filteredOptions.length === 0 &&
        this.search &&
        !this.loading
      )
    },
    isSelectedValue() {
      return this.internalValue.length > 0
    },
    visibleValue() {
      return this.multiple ? this.internalValue.slice(0, this.limit) : []
    },
    deselectLabelText() {
      return this.showLabels ? this.deselectLabel : ''
    },
    selectLabelText() {
      return this.showLabels ? this.selectLabel : ''
    },
    selectedLabelText() {
      return this.showLabels ? this.selectedLabel : ''
    },
    contentStyle() {
      return this.options.length
        ? { display: 'inline-block' }
        : { display: 'block' }
    },
    isAbove() {
      if (this.openDirection === 'above' || this.openDirection === 'top') {
        return true
      } else if (
        this.openDirection === 'below' ||
        this.openDirection === 'bottom'
      ) {
        return false
      } else {
        return this.prefferedOpenDirection === 'above'
      }
    },
    wrapperClasses() {
      return [
        'multiselect',
        {
          'multiselect--mobile':
            this.$_hydrationHelpers_isLayoutMobile && this.withMobileLayout
        },
        { 'multiselect--active': this.isOpen },
        { 'multiselect--filled': this.isSelectedValue },
        { 'multiselect--disabled': this.disabled },
        { 'multiselect--above': this.isAbove }
      ]
    },
    contentWrapperStyles() {
      return { maxHeight: `${this.optimizedHeight}px` }
    }
  }
}
</script>
<style lang="scss" scoped>
$c-gray-lighter: #eee;
$c-selected: #b3d4fc;

/* stylelint-disable */
fieldset[disabled] .multiselect {
  pointer-events: none;
}

.multiselect__spinner {
  position: absolute;
  right: 1px;
  top: 1px;
  width: 48px;
  height: 35px;
  display: block;
}

.multiselect__spinner:before,
.multiselect__spinner:after {
  position: absolute;
  content: '';
  top: 50%;
  left: 50%;
  margin: -8px 0 0 -8px;
  width: 16px;
  height: 16px;
  border-radius: 100%;
  border-color: grey transparent transparent;
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 0 0 1px transparent;
}

.multiselect__spinner:before {
  animation: spinning 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
  animation-iteration-count: infinite;
}

.multiselect__spinner:after {
  animation: spinning 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
  animation-iteration-count: infinite;
}

.multiselect__loading-enter-active,
.multiselect__loading-leave-active {
  transition: opacity 0.4s ease-in-out;
  opacity: 1;
}

.multiselect__loading-enter,
.multiselect__loading-leave-active {
  opacity: 0;
}

.multiselect,
.multiselect__single {
  font-size: 16px;
  touch-action: manipulation;
  color: black;
  resize: none;

  &::placeholder {
    color: #4a4a4a;
  }

  &.required::after {
    margin-left: 2px;
  }
}

.multiselect--mobile .multiselect__tags .multiselect__single {
  @include mobile {
    font-size: 15px;
    line-height: 21px;
    text-align: center;
    color: $c--multiselect-text-color-legacy;
  }
}

.multiselect {
  box-sizing: content-box;
  display: block;
  position: relative;
  width: 100%;
  min-height: 40px;
  text-align: left;
  color: black;
}

.multiselect * {
  box-sizing: border-box;
}

.multiselect:focus {
  outline: none;
}

.multiselect__tags.error {
  border-color: $c--error;
}

.multiselect--active {
  z-index: 50;
}

.multiselect--active:not(.multiselect--above) .multiselect__tags {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.multiselect--active .multiselect__select--arrow {
  top: calc(50% + 3px);
  transform: translate(-50%, -50%) rotateZ(225deg);
}

.multiselect--active {
  .multiselect__select--arrow {
    border-color: $c--main;
  }
}

.multiselect--above.multiselect--active .multiselect__tags {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.multiselect__single {
  position: relative;
  display: inline-block;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  padding: 0 0 0 6px;
  width: 100%;
  transition: border 0.1s ease;
}

.multiselect__tag ~ .multiselect__single {
  width: auto;
}

.multiselect__single:hover {
  border-color: $c--main;
}

.multiselect__single:focus {
  border-color: $c--main;
  outline: none;
}

.multiselect__tags-wrap {
  display: block;
}

.multiselect__tags {
  min-height: 40px;
  display: block;
  padding: 10px 50px 0 8px;
  border-radius: 0;
  border: 2px solid black;
  background: #fff;

  &--active {
    border-color: $c--main;
  }
}

.multiselect__tag {
  position: relative;
  display: inline-block;
  padding: 2px 22px 2px 10px;
  border-radius: 10px;
  border: 2px solid black;
  margin-right: 10px;
  color: black;
  line-height: 1;
  background: white;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
}

.multiselect__tags--active {
  .multiselect__tag {
    color: $c--main;
    border-color: $c--main;
  }
}

.multiselect__tag-icon {
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: initial;
  width: 18px;
  text-align: center;
  transition: all 0.2s ease;
}

.multiselect__tag-icon:after {
  content: '×';
  color: black;
  font-size: 14px;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background: black;
}

.multiselect__tag-icon:focus:after,
.multiselect__tag-icon:hover:after {
  color: white;
}

.multiselect--disabled {
  pointer-events: none;

  .multiselect__tags {
    background-color: $c--disabled-legacy;
    border-color: $c--disabled-border-legacy;

    .text-field--label,
    .multiselect__single {
      background-color: $c--disabled-legacy;
      color: $c--disabled-text-legacy;
    }
  }

  .multiselect__select {
    color: $c--disabled-legacy;

    &--arrow {
      border-color: $c--disabled-border-legacy;
    }
  }
}

.multiselect__tags--active {
  .multiselect__tag-icon {
    color: $c--main;

    &:after {
      color: $c--main;
    }

    &:focus,
    &:hover {
      background: $c--main;

      &:after {
        color: white;
      }
    }
  }
}

.multiselect__select {
  line-height: 16px;
  display: block;
  position: absolute;
  box-sizing: border-box;
  width: 50px;
  height: 40px;
  right: 1px;
  top: 1px;
  padding: 4px 8px;
  margin: 0;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s ease;

  &--arrow {
    width: 12px;
    height: 12px;
    border-bottom: 2px solid;
    border-right: 2px solid;
    border-color: black;
    position: absolute;
    left: 50%;
    top: calc(50% - 3px);
    transform: translate(-50%, -50%) rotate(45deg);
  }
}

.multiselect__placeholder {
  color: #4a4a4a;
  display: inline-block;
  margin-bottom: 10px;
  padding-top: 2px;
}

.multiselect--active .multiselect__placeholder {
  display: none;
}

.multiselect__content-wrapper {
  position: absolute;
  display: block;
  background: #fff;
  width: 100%;
  max-height: 240px;
  overflow: auto;
  border: 2px solid $c--main;
  z-index: 50;
  -webkit-overflow-scrolling: touch;
  border-top: none;
}

.multiselect__content {
  position: relative;
  list-style: none;
  display: inline-block;
  padding: 0;
  margin: 0 !important;
  min-width: 100%;
  vertical-align: top;
}

.multiselect--above .multiselect__content-wrapper {
  bottom: 100%;
  border-bottom: none;
  border-top: 2px solid $c--main;
}

.multiselect__content::webkit-scrollbar {
  display: none;
}

.multiselect__element {
  display: block;

  &:focus {
    .multiselect__option {
      background: $c-gray-lighter;
    }
  }
}

.multiselect__no-result {
  font-size: 16px;
  text-align: center;
}

.multiselect__option {
  display: block;
  padding: 12px;
  min-height: 50px;
  line-height: 22px;
  text-decoration: none;
  text-transform: none;
  vertical-align: middle;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  background-color: white;
  border-bottom: 1px solid $c-gray-lighter;

  &_default-cursor {
    cursor: default;
  }

  /deep/ .checkbox__wrap .checkbox__label {
    font-size: 16px;
    line-height: 22px;
  }
}

.multiselect__search {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
}

.multiselect__option:after {
  top: 0;
  right: 0;
  position: absolute;
  line-height: 50px;
  padding-right: 12px;
  padding-left: 20px;
}

.multiselect__option--highlight {
  background: $c-gray-lighter;
}

.multiselect__option--selected {
  background: $c-selected;
  color: $c--main;
}

.multiselect__option--disabled {
  background: $c--disabled-legacy;
  color: $c--disabled-legacy;
  cursor: text;
  pointer-events: none;
}

.multiselect__option--disabled.multiselect__option--highlight {
  background: lightgray;
}

.multiselect-enter-active,
.multiselect-leave-active {
  transition: all 0.2s ease-in-out;
}

.multiselect-enter,
.multiselect-leave-active {
  opacity: 0;
}

.multiselect__strong {
  margin-bottom: 8px;
  line-height: 20px;
  display: inline-block;
  vertical-align: top;
}

.text-field--label {
  position: absolute;
  left: 10px;
  top: 12px;
  padding: 0 5px;
  color: #4a4a4a;
  background-color: #fff;
  transition: transform 150ms ease-out, font-size 150ms ease-out,
    color 150ms ease-out;
  pointer-events: none;
  transform: translateY(-155%);
  font-size: 12px;
  line-height: 12px;
}

*[dir='rtl'] .multiselect {
  text-align: right;
}

*[dir='rtl'] .multiselect__select {
  right: auto;
  left: 1px;
}

*[dir='rtl'] .multiselect__tags {
  padding: 8px 8px 0px 50px;
}

*[dir='rtl'] .multiselect__content {
  text-align: right;
}

*[dir='rtl'] .multiselect__option:after {
  right: auto;
  left: 0;
}

*[dir='rtl'] .multiselect__clear {
  right: auto;
  left: 12px;
}

*[dir='rtl'] .multiselect__spinner {
  right: auto;
  left: 1px;
}

@keyframes spinning {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(2turn);
  }
}

.multiselect--mobile {
  height: 86px;

  .multiselect__tags {
    height: 100%;
    padding: 13px 31px 0 31px;
  }

  .multiselect__select {
    bottom: 0;
    top: auto;
    height: 31px;
    padding: 0;
    left: calc(50% - 25px);
  }

  .multiselect__select--arrow {
    top: calc(50% - 3px);
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .multiselect__element {
    font-size: 15px;
    text-align: center;
    color: $c--multiselect-text-color-legacy;
    border-bottom: 1px solid lightgrey;

    &:last-child {
      border: none;
    }
  }

  .multiselect__option {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .multiselect__content-wrapper {
    border: 2px solid $c--main;
  }
}

/* stylelint-enable */
</style>
